import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Eye } from "lucide-react";
import { API_BASE_URL, BACKEND_URL } from "../lib/site";
import SEOHead, { websiteSchema } from "../components/SEOHead";

const API = API_BASE_URL;
const CATEGORIES = ["All", "FMCG", "D2C", "Consumer Tech"];
const CASE_STUDY_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='450' viewBox='0 0 800 450'%3E%3Crect width='800' height='450' fill='%23F3F4F6'/%3E%3Ctext x='50%25' y='46%25' text-anchor='middle' fill='%236B7280' font-family='sans-serif' font-size='18' letter-spacing='3'%3ECASE STUDY%3C/text%3E%3Ctext x='50%25' y='56%25' text-anchor='middle' fill='%239CA3AF' font-family='sans-serif' font-size='13'%3EUpload a custom cover image in admin%3C/text%3E%3C/svg%3E";
const isComingSoonCaseStudy = (caseStudy) => caseStudy.presentation_mode === "coming-soon";
const shouldContainCover = (caseStudy) => caseStudy.cover_image?.includes("notion-zoho-product-led-cover");
const sortComingSoonLast = (items) =>
  [...items].sort((a, b) => Number(isComingSoonCaseStudy(a)) - Number(isComingSoonCaseStudy(b)));

export default function CaseStudiesPage() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    axios
      .get(`${API}/case-studies`)
      .then((res) => {
        const sorted = sortComingSoonLast(res.data);
        setCaseStudies(sorted);
        setFiltered(sorted);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const category = searchParams.get("cat");
    if (category) setActiveCategory(category);
  }, [searchParams]);

  useEffect(() => {
    if (activeCategory === "All") {
      setFiltered(sortComingSoonLast(caseStudies));
      return;
    }

    setFiltered(sortComingSoonLast(
      caseStudies.filter((item) => item.industry?.toLowerCase().includes(activeCategory.toLowerCase()))
    ));
  }, [activeCategory, caseStudies]);

  return (
    <div className="pt-16 page-enter">
      <SEOHead
        title="Case Studies - Growth Systems Deconstructed"
        description="Deconstructing growth systems behind companies - not just their outcomes. Across FMCG, Marketplaces, Consumer Tech & D2C."
        url={`${BACKEND_URL}/case-studies`}
        schema={websiteSchema}
      />

      <div className="border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 sm:py-16">
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">Analysis</p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#0A0A0A] mb-4">
            Case Studies
          </h1>
          <p className="text-[#4B5563] max-w-lg text-lg">
            Deconstructing growth systems behind companies - not just their outcomes.
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            Across FMCG, Marketplaces, Consumer Tech & D2C.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {CATEGORIES.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 text-xs font-semibold border transition-colors ${
                activeCategory === category
                  ? "border-[#002FA7] bg-[#002FA7] text-white"
                  : "border-[#E5E7EB] text-[#4B5563] hover:border-[#002FA7]"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((item) => (
              <div key={item} className="border border-[#E5E7EB] h-80 bg-[#F9FAFB] animate-pulse" />
            ))}
          </div>
        ) : caseStudies.length === 0 ? (
          <div className="text-center py-20 text-[#9CA3AF]">Case studies coming soon.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="case-studies-grid">
            {filtered.map((caseStudy) => {
              const isComingSoon = isComingSoonCaseStudy(caseStudy);

              return (
                <div
                  key={caseStudy.id}
                  className={`min-w-0 border border-[#E5E7EB] flex flex-col bg-white ${isComingSoon ? "cursor-default" : "card-hover"}`}
                  data-testid={`case-study-${caseStudy.id}`}
                >
                  <div className={`min-w-0 aspect-[16/9] overflow-hidden ${shouldContainCover(caseStudy) ? "bg-[#8FB6B5]" : ""}`}>
                    <img
                      src={caseStudy.cover_image || CASE_STUDY_PLACEHOLDER}
                      alt={caseStudy.title}
                      className={`w-full h-full max-w-full ${shouldContainCover(caseStudy) ? "object-contain" : "object-cover"}`}
                      onError={(e) => { e.target.src = CASE_STUDY_PLACEHOLDER; }}
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#6B7280] mb-2">
                      {caseStudy.industry}
                    </p>
                    <h2 className="font-['Outfit'] text-xl font-semibold text-[#0A0A0A] mb-3 leading-tight">
                      {caseStudy.title}
                    </h2>

                    <div className="relative flex flex-col flex-1">
                      <div
                        className="flex flex-col flex-1"
                        style={isComingSoon ? { filter: "blur(3px)", opacity: 0.5 } : undefined}
                      >
                        <p className="text-sm text-[#4B5563] leading-relaxed mb-5 flex-1">
                          {caseStudy.description}
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                          {(caseStudy.metrics || []).slice(0, 4).map((metric, index) => (
                            <div key={`${metric.label}-${index}`} className="border border-[#E5E7EB] p-2.5 bg-[#F9FAFB]">
                              <p className="font-['Outfit'] text-lg font-bold text-[#002FA7]">{metric.value}</p>
                              <p className="text-[10px] text-[#6B7280]">{metric.label}</p>
                            </div>
                          ))}
                        </div>

                        <div className="flex gap-2 mt-auto">
                          {isComingSoon ? (
                            <div
                              className="flex-1 border border-[#D1D5DB] text-[#9CA3AF] text-sm font-medium py-2.5 flex items-center justify-center gap-2"
                              data-testid={`preview-btn-${caseStudy.id}`}
                            >
                              <Eye size={14} /> Read
                            </div>
                          ) : (
                            <Link
                              to={`/case-studies/${caseStudy.id}`}
                              className="flex-1 border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium py-2.5 hover:bg-[#0A0A0A] hover:text-white transition-colors flex items-center justify-center gap-2"
                              data-testid={`preview-btn-${caseStudy.id}`}
                            >
                              <Eye size={14} /> Read
                            </Link>
                          )}
                        </div>
                      </div>

                      {isComingSoon && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/55">
                          <span className="bg-[#002FA7] text-white text-[11px] tracking-[0.18em] uppercase font-bold px-4 py-2">
                            Coming Next
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
